// Manage Folders: the Educational Folders Vault. Create folders, drop or pick files into one, search and sort its files,
// download or delete a file, and delete an empty folder. The screenshots show a folder list on the left and the open
// folder's files on the right.
import { useEffect, useRef, useState } from 'react';

import { ConfirmDialog, IconButton, Notice } from 'src/front-components/lead-desk/kit';
import { Icon } from 'src/front-components/lead-desk/icons';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { PageMasthead, ShellCard, ShellSectionLabel } from 'src/front-components/theme/page-chrome';
import { DeskButton, DeskInput, DeskSelect, ErrorLine, LiveStamp } from 'src/front-components/theme/desk-controls';
import { getPalette } from 'src/front-components/theme/ewings-theme';
import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE, v } from 'src/front-components/theme/shell-tokens';
import { useLiveData } from 'src/front-components/utils/desk-data';
import { createFolder, deleteFile, deleteFolder, downloadHref, fetchFiles, fetchFolders, uploadFile } from 'src/front-components/utils/lead-desk-store';
import {
  ALLOWED_FILE_EXTENSIONS,
  FILE_SORTS,
  folderDeleteBlock,
  formatBytes,
  MAX_FILE_BYTES,
  validateFolderName,
  validateUpload,
  visibleFiles,
  type FileRow,
  type FileSort,
  type FolderRow,
} from 'src/utils/lead-desk';

const when = (iso: string): string => new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export const FoldersScreen = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  const folders = useLiveData(fetchFolders, 30_000);
  const list: FolderRow[] = folders.data ?? [];
  const [chosen, setChosen] = useState<string>('');
  const open = list.find((f) => f.id === chosen) ?? list[0] ?? null;
  const files = useLiveData<FileRow[]>(() => (open === null ? Promise.resolve([]) : fetchFiles(open.id)), 20_000);
  const openId = open?.id ?? '';

  // The list of folders arrives after the first render, and the open folder can change; load its files each time.
  useEffect(() => {
    void files.refresh();
  }, [openId, files.refresh]);

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<FileSort>('DATE');
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [removingFile, setRemovingFile] = useState<FileRow | null>(null);
  const [removingFolder, setRemovingFolder] = useState<FolderRow | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement | null>(null);
  const canManage = ctx.profile.canManageFolders;
  const shown = open === null ? [] : visibleFiles(files.data ?? [], open.id, query, sort);

  const reloadAll = async (): Promise<void> => {
    await Promise.all([folders.refresh(), files.refresh()]);
  };

  const makeFolder = async (): Promise<void> => {
    const problem = validateFolderName(name, list);

    setNameError(problem);

    if (problem !== null) return;

    setBusy(true);

    try {
      const folder = await createFolder(name.trim());

      setName('');
      setChosen(folder.id);
      setDone(`Folder "${folder.name}" created.`);
      await folders.refresh();
    } catch (cause) {
      setNameError(cause instanceof Error ? cause.message : 'Could not create the folder.');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (picked: File[]): Promise<void> => {
    if (open === null || picked.length === 0) return;

    setFailure(null);
    setDone(null);
    setBusy(true);

    const problems: string[] = [];
    let added = 0;

    for (const file of picked) {
      const problem = validateUpload(file.name, file.size);

      if (problem !== null) {
        problems.push(problem);
        continue;
      }

      try {
        await uploadFile(open.id, file);
        added += 1;
      } catch (cause) {
        problems.push(cause instanceof Error ? cause.message : `${file.name}: could not upload.`);
      }
    }

    setBusy(false);
    if (picker.current !== null) picker.current.value = '';
    if (added > 0) setDone(`${added} file${added === 1 ? '' : 's'} uploaded to ${open.name}.`);
    if (problems.length > 0) setFailure(problems.join(' '));

    await reloadAll();
  };

  const confirmRemoveFile = async (): Promise<void> => {
    if (removingFile === null) return;

    setBusy(true);
    setRemoveError(null);

    try {
      await deleteFile(removingFile.id);
      setRemovingFile(null);
      await reloadAll();
    } catch (cause) {
      setRemoveError(cause instanceof Error ? cause.message : 'Could not delete the file.');
    } finally {
      setBusy(false);
    }
  };

  const confirmRemoveFolder = async (): Promise<void> => {
    if (removingFolder === null) return;

    setBusy(true);
    setRemoveError(null);

    try {
      await deleteFolder(removingFolder.id);
      setRemovingFolder(null);
      setChosen('');
      await reloadAll();
    } catch (cause) {
      setRemoveError(cause instanceof Error ? cause.message : 'Could not delete the folder.');
    } finally {
      setBusy(false);
    }
  };

  const folderBlock = removingFolder === null ? null : folderDeleteBlock(removingFolder, removingFolder.id === open?.id ? (files.data ?? []) : []);
  const serverCount = removingFolder?.fileCount ?? 0;
  const blockMessage = folderBlock ?? (serverCount > 0 ? `${removingFolder?.name} still holds ${serverCount} file${serverCount === 1 ? '' : 's'}. Delete ${serverCount === 1 ? 'it' : 'them'} first.` : null);

  return (
    <>
      <PageMasthead
        palette={getPalette(ctx.scheme)}
        scheme={ctx.scheme}
        title="Educational Folders Vault"
        subtitle="Upload and organize student documents, spreadsheets, and certificates securely"
        actions={<LiveStamp scheme={ctx.scheme} at={folders.updatedAt} />}
      />
      {done !== null && <Notice tone="green">{done}</Notice>}
      <ErrorLine scheme={ctx.scheme} message={folders.error ?? failure} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: SHELL_SPACE[4], alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SHELL_SPACE[3] }}>
          {canManage && (
            <ShellCard scheme={ctx.scheme}>
              <ShellSectionLabel scheme={ctx.scheme}>Create Folder</ShellSectionLabel>
              <DeskInput scheme={ctx.scheme} value={name} onChange={(value) => { setName(value); setNameError(null); }} placeholder="Folder Name (e.g. Batch 2026)" />
              {nameError !== null && <div style={{ ...SHELL_TYPE.caption, color: v('color-red'), marginTop: SHELL_SPACE[1] }}>{nameError}</div>}
              <div style={{ marginTop: SHELL_SPACE[2] }}>
                <DeskButton scheme={ctx.scheme} primary disabled={busy} onClick={() => void makeFolder()}>
                  + Create
                </DeskButton>
              </div>
            </ShellCard>
          )}

          <ShellCard scheme={ctx.scheme}>
            <ShellSectionLabel scheme={ctx.scheme}>Folders</ShellSectionLabel>
            {list.length === 0 && <div style={{ ...SHELL_TYPE.body, color: shell.textTertiary }}>{folders.loading ? 'Loading…' : canManage ? 'No folders yet. Create the first one above.' : 'No folders yet.'}</div>}
            {list.map((folder) => {
              const on = folder.id === open?.id;

              return (
                <div key={folder.id} style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[1], marginBottom: SHELL_SPACE.half }}>
                  <button
                    type="button"
                    onClick={() => { setChosen(folder.id); setQuery(''); setFailure(null); setDone(null); }}
                    aria-current={on ? 'true' : undefined}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: SHELL_SPACE[2],
                      padding: `${SHELL_SPACE[1.5]} ${SHELL_SPACE[2]}`,
                      border: 'none',
                      borderRadius: SHELL_RADIUS.sm,
                      cursor: 'pointer',
                      textAlign: 'left',
                      ...SHELL_TYPE.body,
                      background: on ? v('background-transparent-light') : 'transparent',
                      color: on ? v('color-blue') : shell.text,
                      fontWeight: on ? 600 : 400,
                    }}
                  >
                    <Icon name="folder" size={15} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.name}</span>
                    <span style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>{folder.fileCount ?? 0}</span>
                  </button>
                  {canManage && <IconButton scheme={ctx.scheme} icon="trash" tone="red" label={`Delete folder ${folder.name}`} onClick={() => { setRemoveError(null); setRemovingFolder(folder); }} />}
                </div>
              );
            })}
          </ShellCard>
        </div>

        <ShellCard scheme={ctx.scheme}>
          {open === null ? (
            <div style={{ ...SHELL_TYPE.body, color: shell.textTertiary, padding: SHELL_SPACE[6], textAlign: 'center' }}>Choose or create a folder to see its files.</div>
          ) : (
            <>
              <ShellSectionLabel scheme={ctx.scheme} action={<span style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>{(files.data ?? []).length} Files</span>}>
                Files in: {open.name}
              </ShellSectionLabel>

              {canManage && (
                <div
                  onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(event) => { event.preventDefault(); setDragging(false); void upload(Array.from(event.dataTransfer.files)); }}
                  onClick={() => picker.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') picker.current?.click(); }}
                  style={{
                    border: `2px dashed ${dragging ? v('color-blue') : shell.borderStrong}`,
                    borderRadius: SHELL_RADIUS.md,
                    padding: SHELL_SPACE[5],
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: dragging ? v('background-transparent-light') : shell.backgroundSecondary,
                    marginBottom: SHELL_SPACE[3],
                  }}
                >
                  <Icon name="upload" size={22} style={{ color: shell.textSecondary }} />
                  <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.text, marginTop: SHELL_SPACE[1] }}>{busy ? 'Uploading…' : 'Drag & drop files here, or click to browse'}</div>
                  <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>
                    Supports {ALLOWED_FILE_EXTENSIONS.map((e) => e.toUpperCase()).join(', ')} (max size {formatBytes(MAX_FILE_BYTES)})
                  </div>
                  <input ref={picker} type="file" multiple accept={ALLOWED_FILE_EXTENSIONS.map((e) => `.${e}`).join(',')} style={{ display: 'none' }} onChange={(event) => void upload(Array.from(event.target.files ?? []))} />
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: SHELL_SPACE[2], alignItems: 'center', marginBottom: SHELL_SPACE[3] }}>
                <DeskInput scheme={ctx.scheme} value={query} onChange={setQuery} placeholder="Search files in folder..." width="280px" />
                <span style={{ ...SHELL_TYPE.label, color: shell.textSecondary }}>Sort by:</span>
                <DeskSelect scheme={ctx.scheme} width="150px" value={sort} onChange={(value) => setSort(value as FileSort)} options={FILE_SORTS} />
              </div>

              {shown.length === 0 && (
                <div style={{ ...SHELL_TYPE.body, color: shell.textTertiary, padding: SHELL_SPACE[4], textAlign: 'center' }}>
                  {(files.data ?? []).length === 0 ? 'This folder is empty.' : 'No files match your search.'}
                </div>
              )}
              {shown.map((file) => (
                <div key={file.id} style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[3], padding: `${SHELL_SPACE[2]} 0`, borderTop: `1px solid ${shell.borderLight}` }}>
                  <Icon name="file" size={18} style={{ color: shell.textSecondary }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
                    <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>
                      {formatBytes(file.size)} · {when(file.uploadedAt)}
                    </div>
                  </div>
                  <a href={downloadHref(file.id)} download={file.name} aria-label={`Download ${file.name}`} style={{ display: 'inline-flex' }}>
                    <span style={{ display: 'inline-flex', width: 28, height: 28, alignItems: 'center', justifyContent: 'center', borderRadius: SHELL_RADIUS.sm, border: `1px solid ${shell.borderMedium}`, color: shell.textSecondary }}>
                      <Icon name="download" size={14} />
                    </span>
                  </a>
                  {canManage && <IconButton scheme={ctx.scheme} icon="trash" tone="red" label={`Delete ${file.name}`} onClick={() => { setRemoveError(null); setRemovingFile(file); }} />}
                </div>
              ))}
            </>
          )}
        </ShellCard>
      </div>

      {removingFile !== null && (
        <ConfirmDialog
          scheme={ctx.scheme}
          title="Delete this file?"
          message={<>This permanently deletes <strong>{removingFile.name}</strong>. It cannot be undone.</>}
          confirmLabel="Delete file"
          busy={busy}
          error={removeError}
          onConfirm={() => void confirmRemoveFile()}
          onCancel={() => setRemovingFile(null)}
        />
      )}
      {removingFolder !== null && (
        <ConfirmDialog
          scheme={ctx.scheme}
          title="Delete this folder?"
          message={blockMessage ?? <>This deletes the empty folder <strong>{removingFolder.name}</strong>.</>}
          confirmLabel={blockMessage === null ? 'Delete folder' : 'OK'}
          busy={busy}
          error={removeError}
          onConfirm={() => (blockMessage === null ? void confirmRemoveFolder() : setRemovingFolder(null))}
          onCancel={() => setRemovingFolder(null)}
        />
      )}
    </>
  );
};
